import { Store } from '@ngrx/store';
import { StoreModule } from '@ngrx/store';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DragulaModule } from 'ng2-dragula';
import { EffectsModule } from '@ngrx/effects';
import { RecaptchaFormsModule } from 'ng-recaptcha/forms';
import { tinymceDefaultSettings } from 'angular-tinymce';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Http, HttpModule, RequestOptions, XHRBackend } from '@angular/http';
import { NgModule, ModuleWithProviders, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { RecaptchaModule, RECAPTCHA_SETTINGS, RecaptchaSettings } from 'ng-recaptcha';

// Services
import { HttpService } from './services/http.service';
import { AccountService } from '../my-account/services/account.service';
import { SessionService } from './services/session.service';
import { TranslationService } from '../translation/services/translation.service';
import { NotificationService } from './services/notification.service';
import { SimpleNotificationService } from './../shared/components/simple-notification/services/SimpleNotification.service';
import { RolesAndPermissionsService } from '../core/services/roles-and-permissions-setup.service';
import { ParticipantStatementSearch } from './services/participant-statement-search.service';
import { AccountStaffService } from '../staff/my-account/services/account.service';
import { ColorPickerModule } from 'ngx-color-picker';

// Modules
import { TranslationModule } from '../translation/translation.module';
import { InternationalPhoneModule } from '../shared/components/intl-phone/intl-phone.module';
import { TinyMceModule, TinyMceComponent } from 'angular-tinymce';
import { SelectComponent, SelectModule } from 'ng2-select';
import { ClipboardModule } from 'ngx-clipboard';
import { AccordionModule, Accordion, AccordionGroup } from 'ngx-accordion';


// Components
import { ModalComponent } from '../shared/components/modal/modal.component';
import { LoaderComponent } from '../shared/components/loader/loader.component';
import { LoaderSpinComponent } from '../shared/components/loader-spin/loader-spin.component';
import { TooltipComponent } from '../shared/components/tooltip/tooltip.component';
import { DropdownComponent } from '../shared/components/dropdown/dropdown.component';
import { DropdownCheckboxComponent } from '../shared/components/dropdown-checkbox/dropdown-checkbox.component';
import { DropdownNumberComponent } from '../shared/components/dropdown-number/dropdown-number.component';
import { DropdownSelectComponent } from '../shared/components/dropdown-select/dropdown-select.component';
import { DropdownPickComponent } from '../shared/components/dropdown-pick/dropdown-pick.component';
import { DropdownTimeComponent } from '../shared/components/dropdown-time/dropdown-time.component';
import { DropdownTableComponent } from '../shared/components/dropdown-table/dropdown-table.component';
import { AppFooterComponent } from '../shared/components/app-footer/app-footer.component';
import { ColorPickerComponent } from '../shared/components/color-picker/color-picker.component';
import { CustomInputComponent } from '../shared/components/custom-input/custom-input.component';
import { LicenseModalComponent } from '../shared/components/license-modal/license-modal.component';
import { PrivacyModalComponent } from '../shared/components/privacy-modal/privacy-modal.component';
import { TermsModalComponent } from '../shared/components/terms-modal/terms-modal.component';
import { HeaderBarComponent } from '../shared/components/header-bar/header-bar.component';
import { UserDropdownComponent } from '../shared/components/header-bar/user-dropdown/user-dropdown.component';
import { NotificationDropdownComponent } from '../shared/components/header-bar/notification-dropdown/notification-dropdown.component';
import { MultiLanguageComponent } from '../shared/components/multi-language/multi-language.component';
import { FilterColumnsComponent } from '../shared/components/filter-columns/filter-columns.component';
import { SimpleNotificationComponent } from '../shared/components/simple-notification/components/simple-notification.component';
import { NewTooltipComponent } from './../shared/components/new-tooltip/new-tooltip.component';
import { ExportModalComponent } from '../shared/components/export-modal/export-modal.component';
import { LockStatusComponent } from './../shared/components/lock-status/lock-status.component';
import { DropdownDownloadComponent } from '../shared/components/dropdown-download/dropdown-download.component';
import { PhoneVerificationModalComponent } from '../shared/components/phone-verification-modal/phone-verification-modal.component';

// Reducers & Effects
import { reducers } from './reducers';
import { SearchEffects } from './effects/search.effects';
import { AccountEffects } from '../my-account/effects/account.effects';
import { AccountStaffEffects } from '../staff/my-account/effects/account.effects';
import { NotificationEffects } from './effects/notification.effects';

// Pipes
import { MomentPipe } from '../shared/pipes/moment/moment.pipe';
import { OrderByPipe } from '../shared/pipes/order-by/order-by.pipe';
import { TranslatePipe } from '../shared/pipes/translate/translate.pipe';
import { TranslateToPipe } from '../shared/pipes/translate-to/translate-to.pipe';
import { EscapeHtmlPipe } from '../shared/pipes/html/keep-html.pipe';
import { PhoneFormatPipe } from '../shared/pipes/phone-format/phone-format.pipe';
import { SerachHighlightPipe } from '../shared/pipes/search-highlight/serach-highlight.pipe';
import { OrderAlphabeticalPipe } from '../shared/pipes/order-by/order-alphabetical.pipe';
import { NotificationDateFormatPipe } from '../shared/pipes/notification-date-format/notification-date-format.pipe';
import { DropdownActionsComponent } from '../shared/components/dropdown-actions/dropdown-actions.component';
import { StatementDateFormatPipe } from '../shared/pipes/statements-date-format/statement-date-format.pipe';
import { PaginationComponent } from '../shared/components/pagination/pagination.component';
import { SafePipe } from '../shared/pipes/safe/safe.pipe';
import { StripHtmlPipe } from '../shared/pipes/stripHtml/stripHtml.pipe';

// Guards
import { RolesGuard, UserPermissionsGuard, ProjectSettingsGuard } from './services/project-setup.guard';

// Factory
export function httpFactory(
  store,
  backend: XHRBackend,
  options: RequestOptions,
  translationService: TranslationService
) {
  return new HttpService(backend, options, store, translationService);
}

@NgModule({
  imports: [
    HttpModule,
    FormsModule,
    RouterModule,
    DragulaModule,
    StoreModule.forFeature('core', reducers),
    EffectsModule.forFeature([
      SearchEffects,
      AccountEffects,
      AccountStaffEffects,
      NotificationEffects
    ]),
    CommonModule,
    RecaptchaModule.forRoot(),
    ReactiveFormsModule,
    RecaptchaFormsModule,
    InternationalPhoneModule,
    // Custom Modules
    TranslationModule,
    TinyMceModule.forRoot(tinymceDefaultSettings()),
    SelectModule,
    ClipboardModule,
    AccordionModule,
    ColorPickerModule
  ],
  exports: [
    HttpModule,
    FormsModule,
    OrderByPipe,
    DragulaModule,
    ModalComponent,
    LoaderComponent,
    LoaderSpinComponent,
    PhoneFormatPipe,
    RecaptchaModule,
    TooltipComponent,
    DropdownComponent,
    DropdownTableComponent,
    DropdownCheckboxComponent,
    DropdownNumberComponent,
    DropdownDownloadComponent,
    SerachHighlightPipe,
    OrderAlphabeticalPipe,
    DropdownSelectComponent,
    DropdownPickComponent,
    DropdownTimeComponent,
    DropdownTableComponent,
    TranslationModule,
    AppFooterComponent,
    ReactiveFormsModule,
    ColorPickerComponent,
    CustomInputComponent,
    LicenseModalComponent,
    PrivacyModalComponent,
    TermsModalComponent,
    UserDropdownComponent,
    NotificationDropdownComponent,
    MultiLanguageComponent,
    HeaderBarComponent,
    RecaptchaFormsModule,
    InternationalPhoneModule,
    TinyMceComponent,
    EscapeHtmlPipe,
    FilterColumnsComponent,
    MomentPipe,
    NotificationDateFormatPipe,
    NewTooltipComponent,
    SimpleNotificationComponent,
    SelectComponent,
    ClipboardModule,
    DropdownActionsComponent,
    LockStatusComponent,
    ExportModalComponent,
    Accordion,
    AccordionGroup,
    StatementDateFormatPipe,
    PhoneVerificationModalComponent,
    PaginationComponent,
    SafePipe,
    StripHtmlPipe,
    TranslateToPipe,
  ],
  declarations: [
    OrderByPipe,
    ModalComponent,
    LoaderComponent,
    LoaderSpinComponent,
    PhoneFormatPipe,
    TooltipComponent,
    DropdownComponent,
    DropdownCheckboxComponent,
    DropdownNumberComponent,
    DropdownTableComponent,
    DropdownDownloadComponent,
    SerachHighlightPipe,
    OrderAlphabeticalPipe,
    DropdownPickComponent,
    DropdownTableComponent,
    DropdownSelectComponent,
    DropdownTimeComponent,
    AppFooterComponent,
    ColorPickerComponent,
    CustomInputComponent,
    LicenseModalComponent,
    PrivacyModalComponent,
    TermsModalComponent,
    UserDropdownComponent,
    NotificationDropdownComponent,
    MultiLanguageComponent,
    HeaderBarComponent,
    EscapeHtmlPipe,
    MomentPipe,
    NotificationDateFormatPipe,
    FilterColumnsComponent,
    NewTooltipComponent,
    SimpleNotificationComponent,
    DropdownActionsComponent,
    LockStatusComponent,
    ExportModalComponent,
    StatementDateFormatPipe,
    PhoneVerificationModalComponent,
    PaginationComponent,
    SafePipe,
    StripHtmlPipe,
    TranslateToPipe
  ],
  providers: [
    HttpService,
    AccountService,
    AccountStaffService,
    NotificationService,
    ParticipantStatementSearch,
    RolesAndPermissionsService,
    {
      provide: RECAPTCHA_SETTINGS,
      useValue: {
        siteKey: '6Lf9KTQUAAAAAGR9J_aNo_3703l-EXb5W3sfu69t'
      } as RecaptchaSettings
    },
    {
      provide: Http,
      useFactory: httpFactory,
      deps: [Store, XHRBackend, RequestOptions, TranslationService]
    },
    SimpleNotificationService,
    RolesGuard,
    UserPermissionsGuard,
    ProjectSettingsGuard
  ],
  schemas: [ CUSTOM_ELEMENTS_SCHEMA ]
})
export class CoreModule {
  static forRoot(): ModuleWithProviders {
    return {
      ngModule: CoreModule,
      providers: [
        MomentPipe,
        HttpService,
        OrderByPipe,
        TranslatePipe,
        TranslateToPipe,
        StripHtmlPipe,
        AccountService,
        EscapeHtmlPipe,
        SessionService,
        PhoneFormatPipe,
        SerachHighlightPipe,
        OrderAlphabeticalPipe,
        NotificationDateFormatPipe,
        ParticipantStatementSearch,
        RolesAndPermissionsService,
        AccountStaffService,
        StatementDateFormatPipe
      ]
    };
  }
}



// WEBPACK FOOTER //
// ./src/app/core/core.module.ts